import React, { useState, useRef, useEffect } from "react";
import { FontColorsOutlined } from "@ant-design/icons";
import { getHeroIcon } from "../../_helpers/heroicons";
import styles from "../styles/QurealRichEditor.module.css";
import { size } from "lodash";

// Text color options - add or modify as needed
const TEXT_COLORS = {
    black: "#000000",
    gray: "#6B7280",
    red: "#EF4444",
    orange: "#F97316",
    amber: "#F59E0B",
    green: "#10B981",
    teal: "#14B8A6",
    blue: "#3B82F6",
    indigo: "#6366F1",
    violet: "#8B5CF6",
    pink: "#EC4899",
    rose: "#F43F5E",
};

const TextColour = ({ editor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Get current text color
    const getCurrentTextColor = () => {
        if (editor.isActive("textStyle")) {
            const attrs = editor.getAttributes("textStyle");
            if (attrs && attrs.color) {
                return attrs.color;
            }
        }
        return null;
    };

    const currentColor = getCurrentTextColor();

    // Apply text color
    const applyTextColor = (color) => {
        editor.chain().focus().setColor(color).run();
        setIsOpen(false);
    };

    // Remove text color
    const removeTextColor = () => {
        editor.chain().focus().unsetColor().run();
        setIsOpen(false);
    };

    return (
        <div className={styles.dropdown} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`${styles.button} ${styles.dropdownToggle} ${
                    editor.isActive("textStyle", { color: currentColor })
                        ? styles.isActive
                        : ""
                }`}
                title="Text color"
            >
                <div className={styles.highlightButtonContainer}>
                    <span className={styles.dropdownLabel}>
                        <FontColorsOutlined
                            style={{ color: currentColor || "currentColor" }}
                        />
                    </span>

                    {/* Color indicator bar */}
                </div>

                {getHeroIcon("chevron-down", { width: "12px" })}
            </button>

            {isOpen && (
                <div className={styles.dropdownMenu}>
                    <div className={styles.colorGrid}>
                        {Object.entries(TEXT_COLORS).map(([name, color]) => (
                            <button
                                key={name}
                                onClick={() => applyTextColor(color)}
                                className={`${styles.colorOption} ${
                                    currentColor === color
                                        ? styles.activeColorOption
                                        : ""
                                }`}
                                style={{ backgroundColor: color }}
                                title={`${name}`}
                            >
                                {currentColor === color && (
                                    <span className={styles.colorCheckmark}>
                                        {getHeroIcon("check", {
                                            width: "14px",
                                            color: getContrastColor(color),
                                        })}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={removeTextColor}
                        className={styles.dropdownItem}
                        disabled={!currentColor}
                    >
                        {getHeroIcon("x-mark", { width: "16px" })}
                        <span>Default text color</span>
                    </button>
                </div>
            )}
        </div>
    );
};

// Helper function to determine text color for checkmarks based on background color
function getContrastColor(hexColor) {
    // For empty or null colors
    if (!hexColor) return "#FFFFFF";

    // Remove # if present
    const hex = hexColor.replace("#", "");

    // Convert to RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate brightness (perceived)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Return white or black based on brightness
    return brightness > 125 ? "#000000" : "#FFFFFF";
}

export default TextColour;
